/**
 * NoteSelection - owns which pitches can sound in the Phraser tool.
 *
 * State is a pitch-class selection (a subset of the 12 pitch classes
 * 0..11), a range window stored as offsets (0..36) within a range track,
 * and an octave offset (-2..+2) that shifts the track as a whole. The
 * track spans C3-C6 (MIDI 48..84), so with the octave offset the
 * reachable register is MIDI 24..108. The sounded set is always derived,
 * never stored: every note in the range window whose pitch class is
 * selected.
 *
 * Rotation is a bijection: rotate(semitones) maps every selected class
 * through mod12(pc + semitones), a reversible permutation of the
 * 12-class domain with no wrap fixup of any kind.
 *
 * The range window is stored as offsets from the track bottom, so the
 * octave carry-along is structural: setOctave changes one field and the
 * window can never leave the track. The setters clamp each thumb against
 * the other, mirroring the dual-thumb slider; low == high is allowed
 * (single-note drilling).
 *
 * Persistence follows the Clicker settings pattern: one localStorage key
 * per field, read at construction, written on change. When localStorage
 * is unavailable (tests), persistence is skipped silently. The dead keys
 * phraser.baseSelection and phraser.transpose are never read or written;
 * stale values are left alone - no migration, no cleanup.
 *
 * See docs/specs/range-window-model.md for the full semantics and
 * rationale.
 */

/** Absolute MIDI note number (60 = C4). */
type MidiNote = number;

/** Chromatic pitch class: 0 = C, 11 = B. `mod12` maps any MIDI note to one. */
type PitchClass = number;

/** Track span: C3-C6 at octave 0, 37 positions (three octaves + one semitone). */
const TRACK_BOTTOM: MidiNote = 48;
const TRACK_TOP: MidiNote = 84;
const TRACK_LENGTH = TRACK_TOP - TRACK_BOTTOM; // 36

/** Default: C major classes, window covering C4-C5 of the C3-C6 track. */
const DEFAULT_PITCH_CLASSES: readonly PitchClass[] = [0, 2, 4, 5, 7, 9, 11];
const DEFAULT_RANGE: readonly [number, number] = [12, 24];

const MIN_OCTAVE = -2;
const MAX_OCTAVE = 2;

const STORAGE_KEYS = {
    pitchClasses: 'phraser.pitchClasses',
    range: 'phraser.range',
    octave: 'phraser.octave',
} as const;

/**
 * Pitch-class names with the real music symbol codepoints (U+266F sharp,
 * U+266D flat), not ASCII # and b - the old phraser did the same via HTML
 * entities. The symbols render via per-glyph font fallback; the note button
 * styles append symbol-capable fonts to the stack for predictable coverage.
 */
const PITCH_CLASS_NAMES = [
    'C', 'C\u266F/D\u266D', 'D', 'D\u266F/E\u266D', 'E', 'F',
    'F\u266F/G\u266D', 'G', 'G\u266F/A\u266D', 'A', 'A\u266F/B\u266D', 'B',
] as const;

const clamp = (value: number, min: number, max: number): number =>
    Math.min(Math.max(value, min), max);

const mod12 = (n: number): number => ((n % 12) + 12) % 12;

class NoteSelection {

    private pitchClasses: Set<PitchClass>;
    private rangeLow: number;
    private rangeHigh: number;
    private octave: number;

    constructor() {
        this.pitchClasses = this.loadPitchClasses();
        [this.rangeLow, this.rangeHigh] = this.loadRange();
        this.octave = clamp(this.loadNumber(STORAGE_KEYS.octave, 0), MIN_OCTAVE, MAX_OCTAVE);
    }

    // ---------------------------------------------------------------------
    // Derivation (pure functions of state)
    // ---------------------------------------------------------------------

    /** The octave-shifted range track: [bottom, top] in absolute MIDI notes. */
    trackWindow(): [MidiNote, MidiNote] {
        const bottom = TRACK_BOTTOM + 12 * this.octave;
        return [bottom, bottom + TRACK_LENGTH];
    }

    /** The range window in absolute MIDI notes (always inside the track). */
    rangeWindow(): [MidiNote, MidiNote] {
        const [trackBottom] = this.trackWindow();
        return [trackBottom + this.rangeLow, trackBottom + this.rangeHigh];
    }

    /**
     * The notes that can actually sound: sorted, every note in the range
     * window whose pitch class is selected. Derived by iterating the
     * window (at most 37 positions) - obviously correct by construction.
     */
    soundedNotes(): MidiNote[] {
        const [low, high] = this.rangeWindow();
        const notes: MidiNote[] = [];
        for (let n = low; n <= high; n++) {
            if (this.pitchClasses.has(mod12(n))) {
                notes.push(n);
            }
        }
        return notes;
    }

    // ---------------------------------------------------------------------
    // Display support
    // ---------------------------------------------------------------------

    /** Checked state for the pitch-class button of class `pc` (0..11). */
    isSelected(pc: PitchClass): boolean {
        return this.pitchClasses.has(pc);
    }

    /** The 12 fixed pitch-class names; labels never move, only checkmarks do. */
    pitchClassLabels(): string[] {
        return [...PITCH_CLASS_NAMES];
    }

    /** The range window as track offsets: thumb positions for the slider. */
    rangeOffsets(): [number, number] {
        return [this.rangeLow, this.rangeHigh];
    }

    /** Current octave offset, -2..+2. */
    getOctave(): number {
        return this.octave;
    }

    // ---------------------------------------------------------------------
    // Mutation (the only way state changes)
    // ---------------------------------------------------------------------

    /**
     * Toggle pitch class `pc` (0..11) in the selection. Selection and
     * range are independent: toggling a class outside the current window
     * is allowed; it simply sounds nothing until the window covers it.
     */
    togglePitchClass(pc: PitchClass) {
        if (this.pitchClasses.has(pc)) {
            this.pitchClasses.delete(pc);
        } else {
            this.pitchClasses.add(pc);
        }
        this.persistPitchClasses();
    }

    /**
     * Rotate the selection: map every selected class through
     * mod12(pc + semitones). A bijection on the 12-class domain - fully
     * reversible, rotate(12) is the identity, rotate(0) a true no-op,
     * no fixup of any kind. `semitones` is unrestricted.
     */
    rotate(semitones: number) {
        const rotated = new Set<PitchClass>();
        for (const pc of this.pitchClasses) {
            rotated.add(mod12(pc + semitones));
        }
        this.pitchClasses = rotated;
        this.persistPitchClasses();
    }

    /** Set the low thumb (track offset), clamped to [0, rangeHigh]. */
    setRangeLow(offset: number) {
        this.rangeLow = clamp(offset, 0, this.rangeHigh);
        this.persistRange();
    }

    /** Set the high thumb (track offset), clamped to [rangeLow, 36]. */
    setRangeHigh(offset: number) {
        this.rangeHigh = clamp(offset, this.rangeLow, TRACK_LENGTH);
        this.persistRange();
    }

    /** Shift the whole track, clamped to -2..+2. The window carries along. */
    setOctave(octaves: number) {
        this.octave = clamp(octaves, MIN_OCTAVE, MAX_OCTAVE);
        this.persist(STORAGE_KEYS.octave, '' + this.octave);
    }

    // ---------------------------------------------------------------------
    // Persistence (one localStorage key per field; skipped when unavailable)
    // ---------------------------------------------------------------------

    private storage(): Storage | null {
        return typeof localStorage === 'undefined' ? null : localStorage;
    }

    private persist(key: string, value: string) {
        this.storage()?.setItem(key, value);
    }

    private persistPitchClasses() {
        this.persist(STORAGE_KEYS.pitchClasses, JSON.stringify([...this.pitchClasses]));
    }

    private persistRange() {
        this.persist(STORAGE_KEYS.range, JSON.stringify(this.rangeOffsets()));
    }

    private loadNumber(key: string, fallback: number): number {
        const stored = this.storage()?.getItem(key);
        return stored == null ? fallback : +stored;
    }

    private loadPitchClasses(): Set<PitchClass> {
        const stored = this.storage()?.getItem(STORAGE_KEYS.pitchClasses);
        if (stored == null) {
            return new Set(DEFAULT_PITCH_CLASSES);
        }
        const classes: PitchClass[] = JSON.parse(stored);
        return new Set(classes.filter(pc => Number.isInteger(pc) && pc >= 0 && pc <= 11));
    }

    private loadRange(): [number, number] {
        const stored = this.storage()?.getItem(STORAGE_KEYS.range);
        if (stored == null) {
            return [...DEFAULT_RANGE];
        }
        const pair: number[] = JSON.parse(stored);
        const low = clamp(Number.isInteger(pair[0]) ? pair[0] : DEFAULT_RANGE[0], 0, TRACK_LENGTH);
        const high = clamp(Number.isInteger(pair[1]) ? pair[1] : DEFAULT_RANGE[1], low, TRACK_LENGTH);
        return [low, high];
    }
}

export {NoteSelection, MidiNote, PitchClass};
